/**
 * G2. Школа 7–14: «Урок». Раз в два года четыре карточки предметов
 * расставить в порядке «что интереснее». Порядок уходит в
 * `flags.lessonOrder` (строка `sport,math,lang,art`), и движок взвешивает
 * по первому предмету школьные события: спорт первым — буллинг реже,
 * математика первой — олимпиада чаще (`LESSON_SPORT_BULLYING_MULT`,
 * `LESSON_MATH_AWARD_MULT` в @life/engine).
 *
 * Знания +1…+3: в школе есть «сильный учитель» по одному предмету
 * (`state.strong`, жребий, игроку показывается как подсказка); поставить его
 * первым → +3, вторым → +2, иначе +1. «Пропустить» — порядок как сдан, +2.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import { FLAG_LESSON_ORDER, type Life, type MinigameOutcome, type Rng } from '@life/engine';
import type { MinigameDef } from '../types';
import { shuffle } from '../skill/common';
import { distinctIndexes, nothing } from './common';

export const LESSON_MIN_AGE = 7;
export const LESSON_MAX_AGE = 14;
export const LESSON_EVERY_YEARS = 2;
export const LESSON_SUBJECTS = ['math', 'lang', 'sport', 'art'] as const;
export type LessonSubject = (typeof LESSON_SUBJECTS)[number];
/** Знания по месту «сильного» предмета в порядке игрока: первым, вторым, третьим, четвёртым. */
export const LESSON_KNOWLEDGE_BY_RANK = [3, 2, 1, 1] as const;
/** Знания при «пропустить». */
export const LESSON_KNOWLEDGE_SKIP = 2;

export interface LessonState {
  /** Предметы в сданном порядке (ключи текста `minigame.lesson.subject.<id>`). */
  subjects: LessonSubject[];
  /** Предмет сильного учителя. */
  strong: LessonSubject;
}

/** Индексы в `state.subjects` в порядке «что интереснее». */
export type LessonInput = { order: number[] };

export function lessonSubjectKey(subject: string): string {
  return `minigame.lesson.subject.${subject}`;
}

export function lessonOrderFlag(order: readonly string[]): string {
  return order.join(',');
}

function result(order: readonly LessonSubject[], knowledge: number): MinigameOutcome {
  return {
    ...nothing(),
    stats: { knowledge },
    flags: { [FLAG_LESSON_ORDER]: lessonOrderFlag(order) },
    logKey: `minigame.lesson.result.${knowledge}`,
    logParams: { first: order[0] ?? '' },
  };
}

export const lesson: MinigameDef<LessonInput, LessonState> = {
  id: 'lesson',
  section: 'G',
  triggers: [{ kind: 'age', min: LESSON_MIN_AGE, max: LESSON_MAX_AGE, everyYears: LESSON_EVERY_YEARS }],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    return life.age >= LESSON_MIN_AGE && life.age <= LESSON_MAX_AGE;
  },
  setup(_life: Life, _ds: Dataset, rng: Rng): LessonState {
    const subjects = shuffle(LESSON_SUBJECTS, rng);
    return { subjects, strong: rng.pick(LESSON_SUBJECTS) };
  },
  play(state: LessonState, input: LessonInput, _life: Life): MinigameOutcome {
    const idx = distinctIndexes(input.order, state.subjects.length);
    for (let i = 0; i < state.subjects.length; i += 1) if (!idx.includes(i)) idx.push(i);
    const order = idx.map((i) => state.subjects[i] as LessonSubject);
    const rank = order.indexOf(state.strong);
    return result(order, LESSON_KNOWLEDGE_BY_RANK[rank] ?? LESSON_KNOWLEDGE_BY_RANK[3]);
  },
  expected(state: LessonState): MinigameOutcome {
    return result(state.subjects, LESSON_KNOWLEDGE_SKIP);
  },
};
